import { graphlib, layout } from '@dagrejs/dagre';
import { useEffect } from 'react';
import { Node, Edge, Position, ReactFlowState, useStore, XYPosition } from 'reactflow';

import { useReactFlow } from '../functions';
import { NodeTypes } from '@/components/FlowTree/flow-tree-service.const';
import {
	NODE_WIDTH,
	X_AXIS_SPACING,
	Y_AXIS_SPACING,
	INTERFACE_FOCUSED_MODE_Y_AXIS_SPACING,
	CLIENT_NODE_Y_AXIS_SPACING,
} from '@/react_app/components/ReactFlow/hooks/react-flow-tree.const';
import { arrayToObject } from '@/utils/helpers';

const { Graph } = graphlib;
const dagreGraph = new Graph();

dagreGraph.setDefaultEdgeLabel(() => ({}));

const DIRECTION = 'TB';

const getYAxisSpacing = (isInterfaceFocusedMode: boolean) =>
	isInterfaceFocusedMode ? INTERFACE_FOCUSED_MODE_Y_AXIS_SPACING : Y_AXIS_SPACING;

const areThereOverlappingNodePositions = (
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	isInterfaceFocusedMode: boolean
) => {
	// Iterate through each node
	for (const nodeId1 in positions) {
		const { x: x1, y: y1 } = positions[nodeId1];
		// Calculate the right boundary of the current node
		const rightBound1 = x1 + NODE_WIDTH;
		// Iterate through other positions to check for overlaps
		const isNode1ClientNode = isClientNode(nodesMap[nodeId1]);
		if (isNode1ClientNode) {
			continue;
		}
		for (const nodeId2 in positions) {
			const isNode2ClientNode = isClientNode(nodesMap[nodeId2]);
			if (isNode2ClientNode) {
				continue;
			}
			if (nodeId1 !== nodeId2) {
				const { x: x2, y: y2 } = positions[nodeId2];
				// Calculate the right boundary of the other node
				const rightBound2 = x2 + NODE_WIDTH;
				// Check for overlap based on X and Y spacing, including right boundaries
				if (
					Math.abs(x1 - x2) < NODE_WIDTH + X_AXIS_SPACING &&
					Math.abs(y1 - y2) < getYAxisSpacing(isInterfaceFocusedMode) &&
					!(x1 > rightBound2 || x2 > rightBound1)
				) {
					// console.error("There are overlapping nodes' positions, please check the layout algorithm", {
					// 	firstNodeId: nodeId1,
					// 	secondNodeId: nodeId2,
					// 	firstNodePosition: positions[nodeId1],
					// 	secondNodePosition: positions[nodeId2],
					// 	positions,
					// });
					return true; // Overlap detected
				}
			}
		}
	}
	return false; // No overlap found
};

const nodeCountSelector = (state: ReactFlowState) => state.nodeInternals.size;
const nodesInitializedSelector = (state: ReactFlowState) =>
	Array.from(state.nodeInternals.values()).every((node) => node.width && node.height);

const sortByRank = (a: Node, b: Node) => {
	// sort by 'rank', - rank is the node's position in the graph, so the higher the rank the lower the node is in the graph, by that, we verify that every node's parent is already positioned and calculated
	// @ts-ignore
	const aRank = dagreGraph.node(a.id)?.rank || 0; // dont know why TS is complaining about rank being undefined, it is defined
	// @ts-ignore
	const bRank = dagreGraph.node(b.id)?.rank || 0; // dont know why TS is complaining about rank being undefined, it is defined
	return aRank - bRank;
};
const isNodeAClusterStart = (node: Node) => {
	const clusterStartNodeTypes = [NodeTypes.BranchRoot, NodeTypes.LoopStart];
	// @ts-ignore
	return clusterStartNodeTypes.includes(node?.type);
};

const getPartialClusterSize = (nodeId: string, clusterSizeMap: Record<string, Record<string, number>> = {}) =>
	clusterSizeMap[nodeId]?.partial || NODE_WIDTH / 2;
const getFullClusterSize = (nodeId: string, clusterSizeMap: Record<string, Record<string, number>> = {}) =>
	clusterSizeMap[nodeId]?.full || NODE_WIDTH;

const getClusterRootDirectedChildrenXsNarrowMode = (
	parentNode: Node,
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	clusterSizeMap: Record<string, Record<string, number>> = {}
) => {
	const res: Record<string, number> = {};

	const orderedChildrenIds = parentNode.data.orderedChildren;
	const siblings = orderedChildrenIds.map((childId: string) => nodesMap[childId]);
	const parentNodeX = positions[parentNode.id]?.x || 0;
	const currentClusterSize = getPartialClusterSize(parentNode.id, clusterSizeMap);
	const distanceBetweenClusterRootToFurthestChildren = currentClusterSize / 2;
	const mostLeftX = parentNodeX - distanceBetweenClusterRootToFurthestChildren;
	const mostRightX = parentNodeX + distanceBetweenClusterRootToFurthestChildren;
	orderedChildrenIds.forEach((childId: string, index: number) => {
		const sibling = nodesMap[childId];
		if (!sibling) {
			return parentNodeX;
		}
		const isFirstChild = index === 0;
		const isLastChild = index === siblings.length - 1;
		const previousSiblingId = orderedChildrenIds[index - 1];
		if (isFirstChild) {
			res[sibling.id] = mostLeftX;
		} else if (isLastChild) {
			res[sibling.id] = mostRightX;
		} else {
			res[sibling.id] =
				res[previousSiblingId] + clusterSizeMap[previousSiblingId]?.right + X_AXIS_SPACING + NODE_WIDTH + clusterSizeMap[sibling.id]?.left;
		}
	});

	return res;
};

const getClusterRootDirectedChildrenXsWideMode = (
	parentNode: Node,
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	clusterSizeMap: Record<string, Record<string, number>> = {}
) => {
	const res: Record<string, number> = {};
	const orderedChildrenIds = parentNode.data.orderedChildren;
	const siblings = orderedChildrenIds.map((childId: string) => nodesMap[childId]).filter((node: Node) => node);
	const parentNodeX = positions[parentNode.id]?.x || 0;
	const widestChildCluster = Math.max(...siblings.map((sibling: Node) => getFullClusterSize(sibling.id, clusterSizeMap)));
	const fullSymmetricClusterWidth = getFullClusterSize(parentNode.data.node.id, clusterSizeMap);
	const leftMostX = parentNodeX - fullSymmetricClusterWidth / 2 + widestChildCluster / 2;
	orderedChildrenIds.forEach((childId: string, index: number) => {
		const sibling = nodesMap[childId];
		if (!sibling) {
			return parentNodeX;
		} else {
			res[sibling.id] = leftMostX + index * (widestChildCluster + X_AXIS_SPACING);
		}
	});

	return res;
};

const getClusterRootDirectChildX = (
	node: Node,
	parentNode: Node,
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	isNarrowMode: boolean,
	clusterSizeMap: Record<string, Record<string, number>> = {}
) => {
	const allSiblingsXs = isNarrowMode
		? getClusterRootDirectedChildrenXsNarrowMode(parentNode, positions, nodesMap, clusterSizeMap)
		: getClusterRootDirectedChildrenXsWideMode(parentNode, positions, nodesMap, clusterSizeMap);
	const res = allSiblingsXs[node.id];
	return res;
};
const getNodeX = (
	node: Node,
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	isNarrowMode: boolean,
	clusterSizeMap: Record<string, Record<string, number>> = {}
) => {
	const nodeIncomingEdges = dagreGraph.inEdges(node.id) || [];
	const graphNode = dagreGraph.node(node.id);
	const parentNode = nodesMap[nodeIncomingEdges[0]?.v];
	let defaultX = graphNode?.x || 0;
	if (parentNode) {
		const parentNodeWidth = parentNode?.width || NODE_WIDTH;
		const parentNodeX = positions[parentNode.id]?.x || 0;
		const parentNodeCenterX = parentNodeX + parentNodeWidth / 2;
		const currentNodeWidth = graphNode?.width || NODE_WIDTH;
		defaultX = parentNodeCenterX - currentNodeWidth / 2;
	}

	const amountOfIncomingEdges = nodeIncomingEdges.length;
	if (amountOfIncomingEdges === 0) {
		return defaultX;
	} else if (amountOfIncomingEdges > 1) {
		return getClusterEndNodeX(node, positions);
	} else if (amountOfIncomingEdges === 1 && parentNode && isNodeAClusterStart(parentNode)) {
		return getClusterRootDirectChildX(node, parentNode, positions, nodesMap, isNarrowMode, clusterSizeMap);
	} else {
		return defaultX;
	}
};
const getClusterEndNodeX = (node: Node, positions: Record<string, XYPosition>) => {
	const clusterStartNodeId = node.data.clusterStartNodeId;
	const clusterStartNodeX = positions[clusterStartNodeId]?.x || 0;
	return clusterStartNodeX;
};
const getNodeY = (node: Node, positions: Record<string, XYPosition>, nodesMap: Record<string, Node>, isInterfaceFocusedMode: boolean) => {
	const nodeIncomingEdges = dagreGraph.inEdges(node.id) || [];
	const yAxisSpacing = getYAxisSpacing(isInterfaceFocusedMode);
	let y = 0;
	if (nodeIncomingEdges.length > 0) {
		const nodeParents = nodeIncomingEdges.map((edge) => ({ ...dagreGraph.node(edge.v), id: edge.v }));
		const isDirectChildOfCluster = nodeParents.some((parent) => isNodeAClusterStart(nodesMap[parent.id]));
		const nodeParentsY = nodeIncomingEdges.map((edge) => positions[edge.v]?.y || 0);
		const nodeParentsHeight = nodeParents.map((parent) => parent?.height || 0) || [];
		const nodeParentsMaxHeight = Math.max(...nodeParentsHeight);
		const nodeParentsMaxY = Math.max(...nodeParentsY);
		const isParentNodeClientNode = nodeParents.some((node) => isClientNode(nodesMap[node.id]));
		if (isDirectChildOfCluster) {
			y = nodeParentsMaxY + nodeParentsMaxHeight + yAxisSpacing + 50;
		} else if (isParentNodeClientNode) {
			y = nodeParentsMaxY + nodeParentsMaxHeight + CLIENT_NODE_Y_AXIS_SPACING;
		} else {
			y = nodeParentsMaxY + nodeParentsMaxHeight + yAxisSpacing;
		}
	}
	return y;
};
// @ts-ignore
const isClientNode = (node: Node) => [NodeTypes.Setup, NodeTypes.ToolTrigger, NodeTypes.AddOnTrigger].includes(node?.type);
const getNodePosition = (
	node: Node,
	positions: Record<string, XYPosition>,
	nodesMap: Record<string, Node>,
	isInterfaceFocusedMode: boolean,
	isNarrowMode: boolean,
	clusterSizeMap: Record<string, Record<string, number>> = {}
) => {
	const x = getNodeX(node, positions, nodesMap, isNarrowMode, clusterSizeMap) || 0;
	const y = getNodeY(node, positions, nodesMap, isInterfaceFocusedMode);
	return { x, y };
};

export function useLayout(
	clusterSizeMap: Record<string, Record<string, number>> = {},
	isNarrowLayout: boolean = false,
	isInterfaceFocusedMode: boolean = false
) {
	const nodeCount = useStore(nodeCountSelector);
	const nodesInitialized = useStore(nodesInitializedSelector);
	const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();

	const elements = useStore(
		(state) => ({
			nodeMap: state.nodeInternals,
			edgeMap: state.edges.reduce((acc, edge) => acc.set(edge.id, edge), new Map()),
		}),
		// The compare elements function will only update `elements` if something has
		// changed that should trigger a layout. This includes changes to a node's
		// dimensions, the number of nodes, or changes to edge sources/targets.
		compareElements
	);

	useEffect(() => {
		// only run the layout if there are nodes and they have been initialized with their dimensions

		if (!nodeCount || !nodesInitialized) {
			return;
		}

		const nodes: Node[] = getNodes();
		const edges: Edge[] = getEdges();

		dagreGraph.setGraph({ rankdir: DIRECTION });

		/* Clear all nodes and edges locations from tree on render of new nodes. if we do not do this it doesn't position the temp node properly */
		const nodeIdsToRemove = dagreGraph.nodes();
		nodeIdsToRemove.forEach((nodeId) => dagreGraph.removeNode(nodeId));
		const edgesToRemove = dagreGraph.edges().filter((edge) => nodeIdsToRemove.includes(edge.v) || nodeIdsToRemove.includes(edge.w));
		edgesToRemove.forEach((edge) => dagreGraph.removeEdge(edge.v, edge.w));

		nodes.forEach((node: Node) => {
			dagreGraph.setNode(node.id, {
				width: node.width ?? 0,
				height: node.height ?? 0,
			});
		});

		edges.forEach((edge: Edge) => {
			dagreGraph.setEdge(edge.source, edge.target);
		});

		layout(dagreGraph);

		setNodes((nodes: Node[]) => {
			let positions: Record<string, XYPosition> = {};
			const nodesMap: Record<string, Node> = arrayToObject(nodes);
			const sortedNodesByRank = nodes.sort(sortByRank);
			const nodesLayoutRes = sortedNodesByRank.map((node) => {
				const { x, y } = getNodePosition(node, positions, nodesMap, isInterfaceFocusedMode, isNarrowLayout, clusterSizeMap);
				positions[node.id] = { x, y };
				return {
					...node,
					style: { opacity: 1 },
					sourcePosition: Position.Bottom,
					targetPosition: Position.Top,
					position: { x, y },
				};
			});
			if (areThereOverlappingNodePositions(positions, nodesMap, isInterfaceFocusedMode)) {
				positions = {};
				return sortedNodesByRank.map((node, index) => {
					const { x, y } = getNodePosition(node, positions, nodesMap, isInterfaceFocusedMode, false, clusterSizeMap);
					positions[node.id] = { x, y };
					return { ...nodesLayoutRes[index], position: { x, y } };
				});
			} else {
				return nodesLayoutRes;
			}
		});

		setEdges((edges: Edge[]) => edges.map((edge) => ({ ...edge, style: { opacity: 1 } })));
	}, [elements, nodesInitialized, clusterSizeMap, isNarrowLayout]);
}

type Elements = {
	nodeMap: Map<string, Node>;
	edgeMap: Map<string, Edge>;
};

function compareElements(xs: Elements, ys: Elements) {
	return compareNodes(xs.nodeMap, ys.nodeMap) && compareEdges(xs.edgeMap, ys.edgeMap);
}

function compareNodes(xs: Map<string, Node>, ys: Map<string, Node>) {
	// the number of nodes changed, so we already know that the nodes are not equal
	if (xs.size !== ys.size) return false;

	for (const [id, x] of xs.entries()) {
		const y = ys.get(id);

		// the node doesn't exist in the next state so it just got added
		if (!y) return false;
		// We don't want to force a layout change while a user might be resizing a
		// node, so we only compare the dimensions if the node is not currently
		// being resized.
		//
		// We early return here instead of using a `continue` because there's no
		// scenario where we'd want nodes to start moving around *while* a user is
		// trying to resize a node or move it around.
		if (x.resizing || x.dragging) return true;
		if (x.width !== y.width || x.height !== y.height) return false;
	}

	return true;
}

function compareEdges(xs: Map<string, Edge>, ys: Map<string, Edge>) {
	// the number of edges changed, so we already know that the edges are not equal
	if (xs.size !== ys.size) return false;

	for (const [id, x] of xs.entries()) {
		const y = ys.get(id);

		// the edge doesn't exist in the next state so it just got added
		if (!y) return false;
		if (x.source !== y.source || x.target !== y.target) return false;
		if (x?.sourceHandle !== y?.sourceHandle) return false;
		if (x?.targetHandle !== y?.targetHandle) return false;
	}

	return true;
}
